import React, { useState, useMemo } from 'react';
import { X, RotateCcw, CheckCircle2 } from 'lucide-react';

import { userStorage } from '../../utils/userStorage';
// null = Frei (leer/blank) → Früh → Spät → Nacht → null
const SCHICHT_CYCLE = ['Früh', 'Spät', 'Nacht']; // null = Frei
const ALL_TYPES     = ['Früh', 'Spät', 'Nacht', 'Frei'];
const SCHICHT_SHORT = { Früh: 'F', Spät: 'S', Nacht: 'N' };
const SCHICHT_COLOR = {
  Früh:  'bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-200',
  Spät:  'bg-blue-100  text-blue-800  border-blue-300  hover:bg-blue-200',
  Nacht: 'bg-purple-100 text-purple-800 border-purple-300 hover:bg-purple-200',
  null:  'bg-white text-slate-200 border-slate-200 hover:bg-slate-50',
};

export const LS_SCHICHT_KEY = 'burningteam_schicht_rhythmus';

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}

function isoWeek(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const jan4 = new Date(d.getFullYear(), 0, 4);
  const sw = new Date(jan4);
  sw.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1);
  return Math.ceil(((d - sw) / 86400000 + 1) / 7);
}

function weekday(dateStr) { // Mon=0 … Sun=6
  return (new Date(dateStr + 'T12:00:00').getDay() + 6) % 7;
}

function daysBetween(a, b) {
  return Math.round((new Date(b + 'T12:00:00') - new Date(a + 'T12:00:00')) / 86400000);
}

/**
 * Erkennt kleinste Wiederholungsperiode (Vielfaches von 7, wochentag-ausgerichtet).
 * HYBRID: Konsistenz-Check + Schichtblock-Grenze.
 *
 * Regeln:
 *  1. Alle eingetragenen Wiederholungstage müssen mit dem ersten Zyklus übereinstimmen.
 *  2. Der letzte eingetragene Tag muss am Ende eines Schichtblocks liegen
 *     (nächster Zyklustag hat eine andere Schicht/Frei).
 *     → Kein vorzeitiges Erkennen mitten im Block (nur "Mo" wenn Mo+Di Früh).
 *     → Erkennung sobald kompletter Startblock wiederholt (z.B. Fr-Sa-So Früh komplett).
 */
function detectPattern(arr) {
  const n = arr.length;
  // arr[n-1] ist immer nicht-null (cycleData trimmt bis letzten nicht-null Tag)
  for (let L = 7; L < n; L += 7) {
    // 1. Konsistenz: alle eingetragenen Wiederholungstage stimmen mit Zyklus überein
    let ok = true;
    for (let i = L; i < n; i++) {
      if (arr[i] !== arr[i % L]) { ok = false; break; }
    }
    if (!ok) continue;

    // 2. Block-Grenze: nächster erwarteter Tag im Zyklus hat andere Schicht
    const lastCyclePos = (n - 1) % L;
    const nextCyclePos = (lastCyclePos + 1) % L;
    if (arr[n - 1] === arr[nextCyclePos]) continue; // noch mitten im Block

    return L;
  }
  return null;
}

export default function SchichtRhythmusModal({ existingRhythmus, onSave, onClose }) {
  const today     = new Date().toISOString().split('T')[0];
  const gridStart = addDays(today, -7);
  const MAX_DAYS  = 42;

  // ── Init ──────────────────────────────────────────────────────
  const initDays = () => {
    const saved = existingRhythmus || (() => {
      try { return JSON.parse(userStorage.getItem(LS_SCHICHT_KEY)); } catch { return null; }
    })();
    const map = {};
    for (let i = 0; i < MAX_DAYS; i++) {
      const d = addDays(gridStart, i);
      if (saved?.days?.length && saved?.cycleLength > 0) {
        const offset = daysBetween(saved.startDate, d);
        if (offset >= 0) {
          const raw = saved.days[offset % saved.cycleLength];
          map[d] = raw === 'Frei' ? null : (raw ?? null); // normalize old "Frei" string → null
        } else {
          map[d] = null;
        }
      } else {
        map[d] = null;
      }
    }
    return map;
  };

  const [days, setDays] = useState(initDays);

  // ── Week grid ─────────────────────────────────────────────────
  const weeks = useMemo(() => {
    const wd       = weekday(gridStart);
    const monStart = addDays(gridStart, -wd);
    const gridEnd  = addDays(gridStart, MAX_DAYS - 1);
    const rows = [];
    let cur = monStart;
    while (cur <= gridEnd) {
      rows.push(Array.from({ length: 7 }, (_, d) => addDays(cur, d)));
      cur = addDays(cur, 7);
    }
    return rows;
  }, [gridStart]);

  // ── Click cycles: null → Früh → Spät → Nacht → null ─────────
  const cycleDay = (d) => {
    setDays(prev => {
      const cur = prev[d];
      const idx = SCHICHT_CYCLE.indexOf(cur);
      const nxt = idx === -1 ? SCHICHT_CYCLE[0]
                : idx === SCHICHT_CYCLE.length - 1 ? null
                : SCHICHT_CYCLE[idx + 1];
      return { ...prev, [d]: nxt };
    });
  };

  // ── Cycle data (first set day → last set day, leading nulls trimmed) ──────
  const cycleData = useMemo(() => {
    let firstIdx = -1, lastIdx = -1;
    for (let i = 0; i < MAX_DAYS; i++) {
      if (days[addDays(gridStart, i)] !== null) {
        if (firstIdx < 0) firstIdx = i;
        lastIdx = i;
      }
    }
    if (lastIdx < 0) return null;
    const arr = [];
    for (let i = firstIdx; i <= lastIdx; i++) arr.push(days[addDays(gridStart, i)]);
    return { days: arr, cycleLength: arr.length, firstIdx };
  }, [days, gridStart]);

  // ── Pattern detection ─────────────────────────────────────────
  const detectedLen = useMemo(() => {
    if (!cycleData) return null;
    return detectPattern(cycleData.days);
  }, [cycleData]);

  // ── Used shift types (including Frei for null days) ───────────
  const usedTypes = useMemo(() => {
    if (!cycleData) return [];
    const set = new Set(cycleData.days.map(d => d ?? 'Frei'));
    return ALL_TYPES.filter(t => set.has(t));
  }, [cycleData]);

  // ── Save ──────────────────────────────────────────────────────
  const handleSave = () => {
    if (!cycleData) return;
    const actualStart = addDays(gridStart, cycleData.firstIdx);
    const saveLen = detectedLen || cycleData.cycleLength;
    const cycleDays = cycleData.days.slice(0, saveLen);
    const rhythmus = {
      startDate:   actualStart,
      cycleLength: saveLen,
      days:        cycleDays,
      savedAt:     new Date().toISOString(),
    };
    userStorage.setItem(LS_SCHICHT_KEY, JSON.stringify(rhythmus));
    onSave(rhythmus, usedTypes);
  };

  const handleReset = () => {
    const map = {};
    for (let i = 0; i < MAX_DAYS; i++) map[addDays(gridStart, i)] = null;
    setDays(map);
  };

  const patternReady = detectedLen !== null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[350] p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 border-b border-slate-100 px-5 py-3 flex items-center justify-between">
          <h2 className="font-bold text-slate-800 text-base">Schichtrhythmus einrichten</h2>
          <div className="flex items-center gap-1">
            <button type="button" onClick={handleReset} title="Zurücksetzen"
              className="p-1.5 text-slate-400 hover:text-red-500 transition-colors">
              <RotateCcw className="w-4 h-4" />
            </button>
            <button type="button" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Info */}
        <div className="flex-shrink-0 px-4 pt-3 pb-1">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-800 leading-relaxed">
            <p className="font-semibold mb-1">So einfach geht's:</p>
            <p>
              Trage einfach deinen Schichtplan ein – tippe auf jeden Tag und wähle deine Schicht.
              Trage so lange ein, bis sich dein Block wiederholt, dann <strong>hör auf</strong>.
              Die App erkennt deinen Rhythmus automatisch!
            </p>
          </div>
        </div>

        {/* Pattern detected banner */}
        {patternReady && (
          <div className="flex-shrink-0 mx-4 mt-2 px-3 py-2 bg-emerald-100 border border-emerald-300 rounded-xl flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <p className="text-xs text-emerald-800 font-medium">
              Muster erkannt: <strong>{detectedLen} Tage</strong> – Du kannst jetzt speichern!
            </p>
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 overflow-y-auto px-4 pb-2 pt-2">
          {/* Column labels */}
          <div className="grid grid-cols-8 gap-1 mb-1 sticky top-0 bg-white py-1 z-10">
            <div className="text-[10px] text-slate-400 text-center">KW</div>
            {['Mo','Di','Mi','Do','Fr','Sa','So'].map(l => (
              <div key={l} className="text-[10px] font-semibold text-slate-500 text-center">{l}</div>
            ))}
          </div>

          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-8 gap-1 mb-1">
              <div className="text-[10px] text-slate-400 flex items-center justify-center">
                {isoWeek(week[0])}
              </div>
              {week.map(d => {
                const inRange = d >= gridStart && d < addDays(gridStart, MAX_DAYS);
                if (!inRange) return <div key={d} />;
                const isToday = d === today;
                const isPast  = d < today;
                const schicht = days[d];
                const colorCls = SCHICHT_COLOR[schicht] ?? SCHICHT_COLOR[null];
                const dayNum  = d.split('-')[2];
                return (
                  <button key={d} type="button" onClick={() => cycleDay(d)}
                    className={`
                      schicht-day-btn relative rounded-lg border text-center py-1 transition-all
                      ${colorCls}
                      ${isToday ? 'ring-2 ring-emerald-500 ring-offset-1 shadow-sm' : ''}
                      ${isPast && !isToday ? 'opacity-55' : ''}
                    `}
                    title={`${d} – ${schicht ?? 'Frei'}`}>
                    <div className={`text-[9px] leading-none ${isToday ? 'font-bold text-emerald-600' : 'text-slate-400'}`}>
                      {dayNum}{isToday && '●'}
                    </div>
                    <div className="text-[11px] font-bold leading-none mt-0.5">
                      {schicht ? SCHICHT_SHORT[schicht] : '·'}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex-shrink-0 px-4 py-2 flex gap-2 flex-wrap border-t border-slate-50 items-center">
          {SCHICHT_CYCLE.map(s => (
            <span key={s} className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${SCHICHT_COLOR[s]}`}>{s}</span>
          ))}
          <span className="px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-white border-slate-200 text-slate-400">· Frei</span>
          <span className="text-[10px] text-slate-400">| Tippen zum Wechseln</span>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-slate-100 px-4 py-3">
          <div className="text-xs text-slate-500 mb-2.5 flex items-center justify-between">
            <span>
              {cycleData
                ? <><strong className="text-slate-700">{cycleData.cycleLength}</strong> Tage eingetragen</>
                : <span className="text-slate-400">Noch keine Tage eingetragen</span>}
            </span>
            {usedTypes.length > 0 && (
              <span className="text-slate-400 text-[10px]">{usedTypes.join(' · ')}</span>
            )}
          </div>
          <button type="button" disabled={!cycleData} onClick={handleSave}
            className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
              patternReady
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm ring-2 ring-emerald-300'
                : cycleData
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
            }`}>
            {patternReady ? `✓ Zyklus abgeschlossen & speichern (${detectedLen} Tage)` : 'Zyklus abgeschlossen & speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
